import { isEqual } from 'lodash'

import { Collection } from '~/models/Collection'
import { SquareSizes } from '~/models/ImageSizes'
import { Track } from '~/models/Track'

import { resolveImageUrl } from './resolveImageUrl'
import { Nullable } from './typeUtils'

const filterTrack = (track1: Track) => (track2: Track) =>
  track1.track_id !== track2.track_id

type ArtworkActions = {
  added?: Track
  removed?: Track
  reordered?: Track[]
  updated?: Track[]
  regenerate?: boolean
}

type Context = {
  generateImage: (urls: string[]) => Promise<{ url: string; file: File }>
}

export const updatePlaylistArtwork = async (
  collection: Collection,
  tracks: Track[],
  actions: ArtworkActions,
  context: Context
) => {
  const { is_image_autogenerated, cover_art_sizes, artwork } = collection

  // If the playlist has custom artwork (either explicitly marked as not autogenerated
  // or has artwork with a file property indicating user upload), don't override it
  // unless explicitly regenerating
  const hasCustomArtwork =
    !is_image_autogenerated &&
    (cover_art_sizes || (artwork && 'file' in artwork))
  if (hasCustomArtwork && !actions.regenerate) {
    return collection
  }

  let tracksForImage: Nullable<Track[]> = null

  if (actions.added) {
    if (tracks.length === 0) {
      tracksForImage = [actions.added]
    } else if (tracks.length === 3) {
      tracksForImage = [...tracks, actions.added]
    }
  } else if (actions.removed) {
    const { removed } = actions
    const removedIndex = tracks.findIndex(
      (track) => track.track_id === removed.track_id
    )
    if (removedIndex === -1) {
      // continue
    } else if (tracks.length > 4 && removedIndex < 4) {
      tracksForImage = tracks.filter(filterTrack(removed)).slice(0, 4)
    } else if (tracks.length === 4) {
      tracksForImage = tracks.filter(filterTrack(removed)).slice(0, 1)
    } else if (tracks.length < 4) {
      if (tracks.length === 1) {
        tracksForImage = []
      } else if (removedIndex === 0) {
        tracksForImage = tracks.slice(1, 2)
      }
    }
  } else if (actions.reordered) {
    if (
      tracks.length >= 4 &&
      !isEqual(actions.reordered.slice(0, 4), tracks.slice(0, 4))
    ) {
      tracksForImage = actions.reordered.slice(0, 4)
    } else if (tracks.length < 4 && !isEqual(actions.reordered[0], tracks[0])) {
      tracksForImage = [actions.reordered[0]]
    }
  } else if (actions.updated) {
    if (actions.updated.length < 4 && tracks.length < 4) {
      if (!isEqual(actions.updated[0], tracks[0])) {
        tracksForImage = [actions.updated[0]]
      }
    } else if (actions.updated.length < 4 && tracks.length >= 4) {
      tracksForImage = [actions.updated[0]]
    } else if (!isEqual(actions.updated.slice(0, 4), tracks.slice(0, 4))) {
      tracksForImage = actions.updated.slice(0, 4)
    }
  } else if (actions.regenerate) {
    if (tracks.length < 4) {
      tracksForImage = tracks.slice(0, 1)
    } else {
      tracksForImage = tracks.slice(0, 4)
    }
  }

  // backfill playlists that don't have cover art
  if (!tracksForImage && !cover_art_sizes) {
    if (tracks.length < 4) {
      tracksForImage = tracks.slice(0, 1)
    } else {
      tracksForImage = tracks.slice(0, 4)
    }
  }

  if (tracksForImage) {
    if (tracksForImage.length === 0) {
      // @ts-expect-error
      collection.cover_art_sizes = undefined
    } else {
      const sizes =
        tracksForImage.length === 1
          ? SquareSizes.SIZE_1000_BY_1000
          : SquareSizes.SIZE_480_BY_480

      const trackUrls = await Promise.all(
        tracksForImage.map(async (track) => {
          const resolvedUrl = await resolveImageUrl({
            artwork: track.artwork,
            targetSize: sizes
          })
          return resolvedUrl ?? ''
        })
      )

      const artwork = await context.generateImage(trackUrls)
      collection.artwork = artwork
      collection.is_image_autogenerated = true

      // Preserve existing cover_art_sizes if it exists, otherwise set a placeholder
      if (!collection.cover_art_sizes && artwork?.file) {
        collection.cover_art_sizes = artwork.url
      }
    }
  }

  return collection
}
